import { Image, Text } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateDiffLiteral } from '../../../lib/utils';
import { OUTDATED_PRICE_DISPLAY } from '../../constants';
import { EmergencyHistoryShape } from '../../prop-types/emergency-status';
import styles from './OutageHistory.module.css';

const cx = classnames.bind(styles);

const reportStatus = {
  true: 'YES',
  false: 'NO',
};

const defaultRecord = {
  imageUrl: 'https://static.gasbuddy.com/web/consumer/profile/user-icon.svg',
  nickname: 'N/A',
  reportStatus: 'N/A',
  stamp: 'visitor',
  updateDate: OUTDATED_PRICE_DISPLAY,
};

const getDateIfValid = (reportDate) => {
  try {
    return getDateDiffLiteral(reportDate);
  } catch (error) {
    // if input string isn't parsable to Date type, it means
    // that default update date string kicked in. As seen during testing,
    // the service sends empty strings as defaults for missing properties.
    return defaultRecord.updateDate;
  }
};

const getUserToDisplay = (nickname, stamp) => {
  if (nickname) {
    return nickname;
  }

  return stamp !== 'member' ? stamp : defaultRecord.nickname;
};

function GridItem({
  as: Element,
  children,
  alignStart,
  className,
}) {
  return (
    <Element
      className={cx(
        'gridItem',
        alignStart ? 'textStart' : 'textCenter',
        { cell: Element === 'td' },
        className,
      )}
    >
      {children}
    </Element>
  );
}

GridItem.propTypes = {
  alignStart: PropTypes.bool,
  as: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
};

GridItem.defaultProps = {
  alignStart: false,
  as: 'td',
  children: undefined,
  className: undefined,
};

function EmergencyStatusRecord({ record }) {
  const { t } = useTranslation(['account', 'miscellaneous']);
  return (
    <Fragment>
      <GridItem alignStart className={cx('avatarAndUserName')}>
        <Image
          lazy
          src={record.imageUrl || defaultRecord.imageUrl}
          alt={t('avatarImage', { ns: 'account', memberId: record.nickname })}
          className={cx('avatar')}
        />
        {getUserToDisplay(record.nickname, record.stamp)}
      </GridItem>
      <GridItem>
        {getDateIfValid(record.updateDate)}
      </GridItem>
      <GridItem>
        <Text bold>
          {reportStatus[record.reportStatus]
            ? t(`actions.${reportStatus[record.reportStatus].toLowerCase()}`, { ns: 'miscellaneous' })
            : t('status.na', { ns: 'miscellaneous' })}
        </Text>
      </GridItem>
    </Fragment>
  );
}

EmergencyStatusRecord.propTypes = {
  record: PropTypes.shape({
    imageUrl: PropTypes.string,
    nickname: PropTypes.string,
    reportStatus: PropTypes.bool,
    stamp: PropTypes.string,
    updateDate: PropTypes.string,
  }),
};

EmergencyStatusRecord.defaultProps = {
  record: defaultRecord,
};

export default function OutageHistory({
  list: historyRecords,
  titleKey,
}) {
  const { t } = useTranslation('station');
  return historyRecords.length
    ? (
      <table className={cx('container')}>
        <thead>
          <tr>
            <GridItem as="th" className={cx('textStart')}>
              <Text bold>
                {t('outage.updatedBy', { ns: 'station' })}
              </Text>
            </GridItem>
            <GridItem as="th">
              <Text bold>
                {t('outage.lastUpdated', { ns: 'station' })}
              </Text>
            </GridItem>
            <GridItem as="th">
              <Text bold>
                {`${t(`outage.${titleKey}`, { ns: 'station' })}?`}
              </Text>
            </GridItem>
          </tr>
        </thead>
        <tbody>
          {historyRecords.map(item => (
            <tr key={item.updateDate}>
              <EmergencyStatusRecord record={item} />
            </tr>
          ))}
        </tbody>
      </table>
    )
    : (
      <Text as="p" className={cx('cell')} color="orange">
        {t(`outage.no${titleKey}History`, { ns: 'station' })}
      </Text>
    );
}

OutageHistory.propTypes = {
  list: PropTypes.arrayOf(EmergencyHistoryShape),
  titleKey: PropTypes.string.isRequired,
};

OutageHistory.defaultProps = {
  list: [],
};
